// src/chatbot/components/MessageBubble.tsx
import React from "react";
import { motion } from "framer-motion";
import type { Branch } from "~/types/new_types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
interface MessageBubbleProps {
  isUser: boolean;
  theme: Branch["theme"];
  children: React.ReactNode;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  isUser,
  theme,
  children,
}) => {
  // Extract text content from children - handle strings, numbers, and React elements
  const messageContent = React.useMemo(() => {
    if (typeof children === 'string' || typeof children === 'number') {
      return String(children);
    }
    if (React.isValidElement(children)) {
      return children; // Return as-is for React elements like AudioPlayer
    }
    // For arrays or other complex structures, try to extract text
    if (Array.isArray(children)) {
      return children.map(child => 
        typeof child === 'string' || typeof child === 'number' ? String(child) : ''
      ).join('');
    }
    return '';
  }, [children]);
  
  // Check if content is a React element (like AudioPlayer)
  const isReactElement = React.isValidElement(messageContent);
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="relative px-4 py-3 rounded-2xl"
      style={{
        backgroundColor: isUser ? theme.primaryColor : theme.cardColor,
        color: isUser ? "#ffffff" : theme.textColor,
        borderRadius: theme.borderRadius,
        border:
          !isUser && theme.cardStyle === "bordered"
            ? `1px solid ${theme.borderColor}`
            : "none",
        boxShadow:
          theme.cardStyle === "shadow"
            ? `0 2px 8px ${theme.primaryColor}20`
            : "none",
      }}
    >
      {isReactElement ? (
        messageContent
      ) : (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Customize markdown elements to match your theme
            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
            code: ({ children }) => (
              <code className="bg-black/10 px-1 py-0.5 rounded text-sm">
                {children}
              </code>
            ),
            pre: ({ children }) => (
              <pre className="bg-black/10 p-2 rounded overflow-x-auto my-2">
                {children}
              </pre>
            ),
          }}
        >
          {String(messageContent)}
        </ReactMarkdown>
      )}
    </motion.div>
  );
};

export default MessageBubble;
